import { addressKind, isWorkspaceApp } from '@/lib/workspace'
import type { AddressBoot, AddressTree, RedirectRule, TenantAddress } from './types'

/*
 * P12 — which route tree a tab gets and where its document goes instead,
 * from what the boot learnt. Pure: the gate (AddressGate) does the
 * `location.replace`, the router (app/router.tsx) only picks.
 *
 *   known, platform            the platform's tree (its `redirect.website` — the www door — first)
 *   known, company, /app…      the workspace (its `redirect.workspace` first)
 *   known, company, other      the token pages the app still serves (its `redirect.website` first);
 *                              the website itself is the site server's (site/)
 *   none (404)                 "There is nothing at this address"
 *   offline                    the platform's address opens its tree (the host mirror of lib/workspace.ts);
 *                              any other address shows the unavailable page with a retry — or, during
 *                              maintenance (503), its usual tree, whose screens show the maintenance notice
 */

type Place = Pick<Location, 'pathname' | 'search' | 'hash' | 'href'>

/** The rule that applies to a path at this address, if any. */
function ruleFor(boot: AddressBoot, pathname: string): RedirectRule | null {
  if (boot.state !== 'known') return null
  const { address } = boot
  if (address.kind === 'platform') return address.redirect.website

  return isWorkspaceApp(pathname) ? address.redirect.workspace : address.redirect.website
}

/**
 * Where this document goes instead of being served, or null: the rule's
 * origin with the path, query and hash kept, or its full URL. A target that
 * is this very page is no redirect (never a loop).
 */
export function redirectTarget(boot: AddressBoot, place: Place): string | null {
  const rule = ruleFor(boot, place.pathname)
  if (rule === null || rule.to === '') return null

  const target = rule.keep_path ? `${rule.to.replace(/\/+$/, '')}${place.pathname}${place.search}${place.hash}` : rule.to

  try {
    return new URL(target, place.href).href === new URL(place.href).href ? null : target
  } catch {
    return null
  }
}

/** The route tree for this tab. */
export function treeFor(boot: AddressBoot, place: Place & Pick<Location, 'hostname'>): AddressTree {
  if (redirectTarget(boot, place) !== null) return 'leaving'

  const app = isWorkspaceApp(place.pathname)

  switch (boot.state) {
    case 'known':
      if (boot.address.kind === 'platform') return 'platform'
      return app ? 'workspace' : 'site'
    case 'none':
      return 'none'
    case 'offline': {
      const kind = addressKind(place.hostname)
      if (kind === 'platform') return 'platform'
      if (boot.maintenance && kind === 'workspace') return app ? 'workspace' : 'site'
      return 'unavailable'
    }
  }
}

/** The company whose address this is, or null (the platform's, nobody's, or not known). */
export const tenantOf = (boot: AddressBoot | null): TenantAddress | null => (boot?.state === 'known' && boot.address.kind === 'tenant' ? boot.address : null)

/** An API-relative asset path (`/api/branding/icon/32?v=…`) on this origin, or an absolute URL as given. */
export function assetUrl(path: string, apiUrl: string): string {
  return path.startsWith('/') ? `${apiUrl}${path}` : path
}
